import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';

import { initialErrorState } from '../../../commons/utils/initial-error-state';
import { retrieveData } from '../../../commons/utils/local-storage-manager';
import { platformCartIdValue } from '../../../helpers/prelaunchData';
import { emptyManualData } from '../../../commons/containers/manual-address/container/utils';
import { httpClient } from '../../../commons/adapter/http-request';
import { emptyAdditionalData } from '../../../commons/utils/empty-additional-data';
import { deliveryMethod } from '../../../commons/constants';

import { findSelectedMethod, areRequiredFieldsReady } from '../utils';

export const sliceKey = 'shipping';

// #region initial state + local storage
const retrieved = retrieveData(sliceKey, platformCartIdValue, {});

const initialState = {
  additionalData: retrieved.additionalData || emptyAdditionalData,
  address: retrieved.address || null,
  addressGoogle: {
    error: initialErrorState,
    value: retrieved.addressGoogle || null,
  },
  addressDownshift: {
    error: initialErrorState,
    value: retrieved.addressDownshift || null,
  },
  addressManual: emptyManualData,
  deliveryMethod: retrieved.deliveryMethod || deliveryMethod.shipping,
  bopis: {
    isEnabled: false,
    pickupMethods: [],
    selectedPickupMethod: retrieved.selectedPickupMethod || null,
  },
  fullName: {
    error: initialErrorState,
    value: retrieved.fullName || '',
  },
  error404Timestamp: 0,
  methods: {
    error: initialErrorState,
    selectedKey: retrieved.selectedMethod || '',
    list: [],
  },
  status: {
    isReady: false,
    isLoadingWithShippingAddress: false,
    isLoadingWithShippingMethod: false,
    isPaymentDataReady: true,
    isLoadingWithPickupMethod: false,
  },
};

// Empty state is needed so we can always get a fresh, clear state when we reset the data
const emptyState = {
  additionalData: emptyAdditionalData,
  address: null,
  addressGoogle: {
    error: initialErrorState,
    value: null,
  },
  addressDownshift: {
    error: initialErrorState,
    value: null,
  },
  addressManual: emptyManualData,
  deliveryMethod: deliveryMethod.shipping,
  bopis: {
    isEnabled: false,
    pickupMethods: [],
    selectedPickupMethod: null,
  },
  fullName: {
    error: initialErrorState,
    value: '',
  },
  methods: {
    error: initialErrorState,
    selectedKey: '',
    list: [],
  },
  status: {
    isReady: false,
    isLoadingWithShippingAddress: false,
    isLoadingWithShippingMethod: false,
    isPaymentDataReady: true,
    isLoadingWithPickupMethod: false,
  },
};
// #endregion

// #region api requests
export const postWithShippingAddress = createAsyncThunk(
  `${sliceKey}/postWithShippingAddress`,
  async (payload, { signal, rejectWithValue }) => {
    try {
      const source = httpClient.CancelToken.source();
      signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = await httpClient.request({
        method: 'POST',
        url: `/carts/${payload.cartId}/with_shipping_address`,
        data: payload.data,
        cancelToken: source.token,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        data: err.response.data,
      });
    }
  },
);

export const postWithShippingMethod = createAsyncThunk(
  `${sliceKey}/postWithShippingMethod`,
  async (payload, { signal, rejectWithValue }) => {
    try {
      const source = httpClient.CancelToken.source();
      signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = await httpClient.request({
        method: 'POST',
        url: `/carts/${payload.cartId}/with_shipping_method`,
        data: payload.data,
        cancelToken: source.token,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        data: err.response.data,
      });
    }
  },
);

export const postWithPickupMethod = createAsyncThunk(
  `${sliceKey}/postWithPickupMethod`,
  async (payload, { signal, rejectWithValue }) => {
    try {
      const source = httpClient.CancelToken.source();
      signal.addEventListener('abort', () => {
        source.cancel();
      });
      const response = await httpClient.request({
        method: 'POST',
        url: `/carts/${payload.cartId}/with_pickup_method`,
        data: payload.data,
        cancelToken: source.token,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue({
        status: err.response.status,
        data: err.response.data,
      });
    }
  },
);
// #endregion

// #region slice
export const shippingSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    setFullName: (state, action) => {
      state.fullName.value = action.payload.value;
      state.fullName.error = action.payload.error;
    },
    setAddress: (state, action) => {
      state.address = action.payload.value;
    },
    setAddressGoogle: (state, action) => {
      state.addressGoogle.error = action.payload.addressGoogle.error;
      state.addressGoogle.value = action.payload.addressGoogle.value;
    },
    setAddressDownshift: (state, action) => {
      state.addressDownshift.error = action.payload.addressDownshift.error;
      state.addressDownshift.value = action.payload.addressDownshift.value;
    },
    setAddressManual: (state, action) => {
      state.addressManual = action.payload;
    },
    setDeliveryMethod: (state, action) => {
      state.deliveryMethod = action.payload;
    },
    setPickupMethods: (state, action) => {
      state.bopis.pickupMethods = action.payload;
      state.bopis.isEnabled = action.payload?.length > 0;
    },
    setSelectedPickupMethod: (state, action) => {
      state.bopis.selectedPickupMethod = action.payload;
    },
    setAdditionalData: (state, action) => {
      state.additionalData = action.payload;
    },
    setMethods: (state, action) => {
      state.methods.error = action.payload.error;
      state.methods.selectedKey = action.payload.selectedKey;
      state.methods.list = action.payload.list;
    },
    setStatus: (state, action) => {
      const currentIsReady = current(state.status).isReady;
      const currentIsLoadingWithShippingAddress = current(state.status).isLoadingWithShippingAddress;
      const currentIsLoadingWithShippingMethod = current(state.status).isLoadingWithShippingMethod;
      state.status.isReady = typeof action.payload.isReady === 'boolean' ? action.payload.isReady : currentIsReady;
      state.status.isLoadingWithShippingAddress =
        typeof action.payload.isLoadingWithShippingAddress === 'boolean'
          ? action.payload.isLoadingWithShippingAddress
          : currentIsLoadingWithShippingAddress;
      state.status.isLoadingWithShippingMethod =
        typeof action.payload.isLoadingWithShippingMethod === 'boolean'
          ? action.payload.isLoadingWithShippingMethod
          : currentIsLoadingWithShippingMethod;
    },
    resetShipping: state => {
      state.fullName = emptyState.fullName;
      state.address = emptyState.address;
      state.addressGoogle = emptyState.addressGoogle;
      state.addressDownshift = emptyState.addressDownshift;
      state.addressManual = emptyState.addressManual;
      state.additionalData = emptyState.additionalData;
      state.methods = emptyState.methods;
      state.status = emptyState.status;
    },
  },
  extraReducers(builder) {
    const postWithShippingRejectedCase = (state, action) => {
      if (!action.payload) {
        state.status.isLoadingWithShippingMethod = true;
      } else if (action.payload.status === 404) {
        state.error404Timestamp = new Date().getTime();
        state.status.isLoadingWithShippingAddress = true;
        state.status.isLoadingWithShippingMethod = true;
      } else if (action.payload.data.success === false) {
        state.status.isLoadingWithShippingAddress = false;
        state.status.isLoadingWithShippingMethod = false;
        state.methods.error = {
          hasError: true,
          message: action.payload.data.customMessage,
        };
        state.methods.selectedKey = '';
        state.methods.list = [];
      } else if (action.payload.data.errorCode?.id?.indexOf('NF_CHECKOUT') > -1) {
        state.status.isLoadingWithShippingAddress = false;
        state.status.isLoadingWithShippingMethod = false;
        state.methods.error = {
          hasError: !!action.error,
          message: action.error.message,
        };
        state.methods.selectedKey = '';
        state.methods.list = [];
      }
      state.status.isReady = false;
    };
    builder.addCase(postWithShippingAddress.pending, state => {
      state.status.isLoadingWithShippingAddress = true;
      state.status.isLoadingWithShippingMethod = false;
      state.status.isReady = false;
    });
    builder.addCase(postWithShippingMethod.pending, state => {
      state.status.isLoadingWithShippingAddress = false;
      state.status.isLoadingWithShippingMethod = true;
      state.status.isReady = false;
    });
    builder.addCase(postWithPickupMethod.pending, state => {
      state.status.isLoadingWithPickupMethod = true;
      state.status.isReady = false;
    });
    builder.addCase(postWithShippingAddress.rejected, postWithShippingRejectedCase);
    builder.addCase(postWithShippingMethod.rejected, postWithShippingRejectedCase);
    builder.addCase(postWithPickupMethod.rejected, state => {
      state.status.isLoadingWithPickupMethod = false;
      state.status.isReady = false;
    });
    builder.addCase(postWithShippingAddress.fulfilled, (state, action) => {
      const fullName = state.fullName.value;
      const address = state.address?.userInputSingleLine;
      state.status.isLoadingWithShippingAddress = false;
      if (action.payload.shipMethods.length === 0) {
        state.status.isLoadingWithShippingMethod = false;
        state.status.isReady = false;
        state.methods.error = {
          hasError: true,
          message: action.meta.arg.i18n.message1,
        };
        state.methods.selectedKey = '';
        state.methods.list = [];
      } else {
        state.methods.error = initialErrorState;
        state.methods.selectedKey = findSelectedMethod({
          list: action.payload.shipMethods,
          selectedKey: current(state.methods).selectedKey,
        });
        state.methods.list = action.payload.shipMethods;
        state.status.isReady = areRequiredFieldsReady(fullName, address, state.methods.list, state.methods.selectedKey);
      }
    });
    builder.addCase(postWithShippingMethod.fulfilled, (state, action) => {
      const fullName = state.fullName.value;
      const address = state.address?.userInputSingleLine;
      const methods = state.methods.list;
      const selectedMethodKey = state.methods.selectedKey;
      state.status.isLoadingWithShippingAddress = false;
      state.status.isLoadingWithShippingMethod = false;
      state.status.isReady = areRequiredFieldsReady(fullName, address, methods, selectedMethodKey);
      state.methods.selectedKey = action.payload.shipMethodKey;
    });
    builder.addCase(postWithPickupMethod.fulfilled, state => {
      state.status.isLoadingWithPickupMethod = false;
      state.status.isReady = true;
    });
  },
});
// #endregion

export const {
  setFullName,
  setAddress,
  setAddressGoogle,
  setAddressDownshift,
  setAddressManual,
  setDeliveryMethod,
  setPickupMethods,
  setSelectedPickupMethod,
  setAdditionalData,
  setMethods,
  setStatus,
  resetShipping,
} = shippingSlice.actions;

export const selectFullName = state => state.shipping.fullName;
export const selectAddress = state => state.shipping.address;
export const selectAddressGoogle = state => state.shipping.addressGoogle;
export const selectAddressDownshift = state => state.shipping.addressDownshift;
export const selectAdditionalData = state => state.shipping.additionalData;
export const selectMethods = state => state.shipping.methods;
export const selectStatus = state => state.shipping.status;
export const selectCardAddress = state => ({
  fullName: state.shipping.fullName.value,
  address: state.shipping.address?.userInputSingleLine || '',
  apartmentUnit: state.shipping.address?.apartmentUnit || '',
  companyName: state.shipping.address?.companyName || '',
});
export const selectShipping = state => state.shipping;

export default shippingSlice.reducer;
